/**
 * Planning a pasted batch before anything is looked up.
 *
 * Every line gets a stable id (its position) and a verdict: unreadable lines are
 * `invalid`, repeats of an earlier line are `duplicate`, lines past the per-run cap are
 * `over`, and the rest are `pending` — the only ones sent anywhere. A lookup then turns
 * `pending` into `found` or `failed`.
 */

export type BatchState =
  | "invalid"
  | "duplicate"
  | "over"
  | "pending"
  | "found"
  | "failed";

export interface BatchLine<P> {
  id: number;
  input: string;
  parsed: P;
  state: BatchState;
}

/**
 * @param identity The key two lines share when they name the same thing, or null when
 *   the line is not readable at all.
 */
export function planBatch<P>(
  lines: readonly string[],
  parse: (line: string) => P,
  identity: (parsed: P) => string | null,
  max: number
): BatchLine<P>[] {
  const seen = new Set<string>();
  let queued = 0;
  return lines.map((input, id) => {
    const parsed = parse(input);
    const key = identity(parsed);
    if (key === null) {
      return { id, input, parsed, state: "invalid" };
    }
    if (seen.has(key)) {
      return { id, input, parsed, state: "duplicate" };
    }
    seen.add(key);
    queued += 1;
    return { id, input, parsed, state: queued > max ? "over" : "pending" };
  });
}
